import { AnalyticsUserEvent } from '@fruitful/types';
import { UserEventHandler } from '@sendbird/chat';
import type { UnreadMessageCount } from '@sendbird/chat/groupChannel';
import { useFeatureGate } from '@statsig/expo-bindings';
import { usePathname } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { BodySmallEmphasized, Icon, MessagesSquareIcon, View } from 'smoothie';

import { useUserAuthorization } from '../../auth/hooks/useUserAuthorization';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import useSendbird from '../../guideMessaging/hooks/useSendbird';
import { useShowGuideMessaging } from '../../guideMessaging/hooks/useShowGuideMessaging';
import { captureException, track } from '../../logger/Logger';
import { useAuthedUser } from '../providers/AuthedUserProvider';

const getClientId = () => {
    if (window != null) {
        // Simple unique id for each tab/window
        window.name = `${window.name ?? new Date().toISOString()}${Math.random()}`;
        return window.name;
    }
    return 'onDevice';
};

export const MessagingBadgeButton = () => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const { guideMessaging: hasAccessToGuideMessaging } =
        accessToRestrictedCapabilities;
    const hasGuide = user.membership?.guide != null;
    const { sdk: sendbirdSdk, isLoading: isLoadingSendbird } = useSendbird();
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const showGuideMessagingModal = useShowGuideMessaging();
    const pathname = usePathname();
    const clientId = getClientId();

    const isGuideMessagingReady =
        hasGuide && !isLoadingSendbird && sendbirdSdk != null;
    const canShowMessaging = hasAccessToGuideMessaging && isGuideMessagingReady;

    // Initially fetch the unread message count.
    useEffect(() => {
        if (!canShowMessaging) {
            return;
        }

        const getUnreadCount = async () => {
            const unreadCount =
                await sendbirdSdk.groupChannel.getTotalUnreadMessageCount();
            setUnreadMessageCount(unreadCount);
        };

        getUnreadCount().catch((e) => {
            captureException(e);
        });
    }, [canShowMessaging, sendbirdSdk]);

    // Listen for changes to unread count across all clients,
    useEffect(() => {
        if (!canShowMessaging) {
            return;
        }

        // The UNIQUE_HANDLER_ID is a unique identifier you should declare to register multiple concurrent handlers.
        // Include clientId to ensure uniqueness across each tab/window, and pathname to ensure uniqueness for this button on each page
        const UNIQUE_HANDLER_ID = `${clientId}-${pathname.replace('/', '')}-mbb-onTotalUnreadMessageCountChanged-${user.id}`;

        const userEventHandler = new UserEventHandler({
            onTotalUnreadMessageCountChanged: (
                unreadMessageCount: UnreadMessageCount,
            ) => {
                setUnreadMessageCount(unreadMessageCount.groupChannelCount);
            },
        });

        sendbirdSdk.addUserEventHandler(UNIQUE_HANDLER_ID, userEventHandler);

        return () => {
            sendbirdSdk.removeUserEventHandler(UNIQUE_HANDLER_ID);
        };
    }, [canShowMessaging, sendbirdSdk, clientId, pathname, user.id]);

    // Under GOYO, hide the messaging button entirely when the member has no
    // guide assigned — there's no one to message yet. (Non-GOYO behavior is
    // unchanged: the button shows whenever the capability is present.)
    if (!hasAccessToGuideMessaging || (goyoEnabled && !hasGuide)) {
        return null;
    }

    return (
        <View
            paddingHorizontal={8}
            onPress={() => {
                showGuideMessagingModal();
                track(AnalyticsUserEvent.OPEN_MESSAGING, {
                    pathname,
                });
            }}
            cursor="pointer"
        >
            <Icon icon={MessagesSquareIcon} color="$black" />
            {unreadMessageCount > 0 && (
                <View
                    position="absolute"
                    top={-8}
                    right={0}
                    height={20}
                    width={20}
                    borderRadius={100}
                    backgroundColor="$green900"
                    justifyContent="center"
                    alignItems="center"
                >
                    <BodySmallEmphasized color="$white">
                        {unreadMessageCount}
                    </BodySmallEmphasized>
                </View>
            )}
        </View>
    );
};
